import { ErrorMessage, TaInput } from '@components/shared';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { UserCard } from '@features/dashboard/specialistDashboard/userCard/userCard';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, useUpdateUser, useUsersStore } from 'services/users';
import * as Yup from 'yup';

interface UserDetailsProps {
	firstName: string;
	lastName: string;
	showEdit?: boolean;
	user?: User | null;
}

const nameValidationSchema = Yup.object({
	firstName: Yup.string().required('fieldNameError'),
	lastName: Yup.string().required('fieldNameError'),
});

const initialValues = {
	firstName: '',
	lastName: '',
};

export const UserCardWrapper = ({
	firstName,
	lastName,
	showEdit = false,
	user: userProp,
}: UserDetailsProps) => {
	const { t } = useTranslation();
	const mutate = useUpdateUser();

	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const { user: storeUser } = useUsersStore();
	const [isLoading, setIsLoading] = useState(false);

	// Use provided user prop or fallback to store user
	const user = userProp;

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: nameValidationSchema,
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				if (user?.company) {
					await mutate({
						companies: [
							{
								company_id: user.company.company_id,
								company_name: values.firstName,
							},
						],
					});
				} else {
					await mutate({
						first_name: values.firstName,
						last_name: values.lastName,
					});
				}
				setOpen(false);
				setSnackbarOpen(true);
			} catch (error) {
				console.error('Update failed:', error);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const { setValues } = formik;

	useEffect(() => {
		setValues({
			firstName: user?.company ? user.company.company_name : firstName,
			lastName: lastName,
		});
	}, [firstName, lastName, setValues, user?.company]);

	return (
		<UserCard onEdit={() => setOpen(true)} showEdit={showEdit} user={user}>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				saveButtonLoading={isLoading}
				disableSaveButton={isLoading}
				onClose={() => setOpen(false)}
				title={t(!user?.company ? 'fullName' : 'companyName')}
				showActions
			>
				<div className="flex flex-col gap-[10px] mt-[20px]">
					{!user?.company ? (
						<span className="roboto-regular text-[16px] leading-[140%]">{t('myName')}</span>
					) : (
						<span className="roboto-regular text-[16px] leading-[140%]">{t('companyNameIs')}</span>
					)}
					<div>
						<TaInput
							icon={<PersonOutlineOutlinedIcon />}
							id="firstName"
							label={t('firstName')}
							size="small"
							isRequired
							value={formik.values.firstName}
							onChange={formik.handleChange}
							error={formik.touched.firstName && Boolean(formik.errors.firstName)}
						/>
						{formik.touched.firstName && formik.errors.firstName && (
							<ErrorMessage
								message={t(formik.errors.firstName || '', {
									fieldName: t('firstName'),
								})}
							/>
						)}
					</div>
					{!user?.company && (
						<div>
							<TaInput
								icon={<PersonOutlineOutlinedIcon />}
								id="lastName"
								label={t('lastName')}
								size="small"
								value={formik.values.lastName}
								onChange={formik.handleChange}
								error={formik.touched.lastName && Boolean(formik.errors.lastName)}
							/>
							{formik.touched.lastName && formik.errors.lastName && (
								<ErrorMessage
									message={t(formik.errors.lastName || '', {
										fieldName: t('lastName'),
									})}
								/>
							)}
						</div>
					)}
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('fullName'),
				})}
			/>
		</UserCard>
	);
};
